"use client";
import { OnboardingContent } from "@/components/Onboarding/components/OnboardingContent";
import { useQuestCompletions } from "@/components/QuestsV2/useQuestCompletions";
import { OnboardingPrizeWheelModal } from "@/components/ui/custom/prize-wheel/OnboardingPrizeWheelModal";
import { IMMUTABLE_GAME_ID, ONBOARDING_QUEST_ID } from "@/constants";
import { usePassportProvider } from "@/context";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { useOnboardingAnalytics } from "@/context/AnalyticsProvider/onboarding";
import { useLoginModal } from "@/context/LoginModalContext";
import { useAccessToken } from "@/hooks";
import { submitQuestCompletion } from "@/hooks/quest/useSubmitQuest";
import { useReferralAcceptance } from "@/hooks/useReferralAcceptance";
import { notifyError } from "@/utils/monitoring";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

export default function OnboardingContainer() {
  const searchParams = useSearchParams();
  const referralCode = searchParams?.get("ref")?.trim();
  // Parse fb ad if any
  const signUpTitle = referralCode
    ? "Accept your referral, earn from a $60k rewards pool"
    : searchParams?.get("title")?.trim();
  const { isLoggedIn: isWalletLoggedIn, passportClient } =
    usePassportProvider();
  const router = useRouter();
  const { track } = useOnboardingAnalytics();
  const hasViewedRef = useRef(false);
  const onboardingCompletedRef = useRef(false);
  const { openLoginModal } = useLoginModal();
  const { handleAcceptReferral } = useReferralAcceptance();
  const getAccessToken = useAccessToken();

  // State for Prize Wheel Modal (for referral flow)
  const [showPrizeWheel, setShowPrizeWheel] = useState(false);

  const { completedQuests, isLoading } = useQuestCompletions(
    IMMUTABLE_GAME_ID,
    true,
  );

  const isOnboardingCompleted = useMemo(() => {
    return Object.keys(completedQuests || {}).includes(
      ONBOARDING_QUEST_ID.replace("-event", ""),
    );
  }, [completedQuests]);

  const isLoggedOut =
    isWalletLoggedIn !== undefined && isWalletLoggedIn === false;

  useEffect(() => {
    if (hasViewedRef.current) return;
    hasViewedRef.current = true;
    track({
      action: Action.VIEWED,
      extras: {
        id: "onboarding-container",
      },
    });

    // Store fbclid and referrer for future use
    const fbclid = searchParams?.get("fbclid");
    if (fbclid) {
      localStorage.setItem("fbclid", fbclid);
    }
    localStorage.setItem("referrer", document.referrer);
  }, [searchParams, track]);

  // Redirect to quests if onboarding is completed
  useEffect(() => {
    if (isLoading) return;
    if (isOnboardingCompleted) router.replace("/quests");
  }, [isOnboardingCompleted, router, isLoading]);

  // Show Prize Wheel Modal when referralCode is present and user is logged out
  useEffect(() => {
    if (referralCode && isLoggedOut) {
      setShowPrizeWheel(true);
    }
  }, [referralCode, isLoggedOut]);

  // Complete onboarding quest and redirect to homepage after login
  const completeOnboardingAndRedirect = async () => {
    if (onboardingCompletedRef.current) {
      return;
    }

    try {
      onboardingCompletedRef.current = true;

      const error = await submitQuestCompletion(
        ONBOARDING_QUEST_ID,
        getAccessToken,
      );

      if (!error) {
        // Redirect to homepage
        router.replace("/quests");
      } else {
        onboardingCompletedRef.current = false;
        notifyError(error, "questCompletionError", {
          questId: ONBOARDING_QUEST_ID,
        });
      }
    } catch (error) {
      onboardingCompletedRef.current = false;
      notifyError(error, "questCompletionError", {
        questId: ONBOARDING_QUEST_ID,
      });
    }
  };

  const handlePostLogin = async () => {
    // CRITICAL: Check if user actually logged in (not just cancelled)
    // When user clicks X to cancel, the login promise still resolves!
    // We need to verify they're actually logged in before proceeding
    // Note: isLoggedIn state might not have updated yet, so we check access token instead
    const token = await passportClient.getAccessToken();

    if (!token) {
      return;
    }

    // Trigger the zk address generation by calling eth_requestAccounts.
    // The token will be refreshed and include the zk address in the claims
    // after getAccessToken is called.
    const passportProvider = await passportClient.connectEvm();
    await passportProvider.request({
      method: "eth_requestAccounts",
    });

    // Auto-accept referral if present
    if (referralCode) {
      track({
        action: Action.PRESSED,
        extras: {
          control: "AcceptReferral_AutoAccept",
          controlType: ControlType.BUTTON,
          id: "referral",
        },
      });

      const success = await handleAcceptReferral(referralCode);
      if (!success) {
        console.error("Failed to accept referral, continuing onboarding");
      }
    }

    // Close prize wheel modal after successful login
    setShowPrizeWheel(false);

    // Complete onboarding quest and redirect to homepage
    await completeOnboardingAndRedirect();
  };

  // Handle prize wheel completion - trigger login modal
  const handlePrizeWheelComplete = () => {
    // Open login modal - callback only runs on successful login
    openLoginModal(handlePostLogin);
  };

  const handleClosePrizeWheel = () => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "PrizeWheel_Close",
        controlType: ControlType.BUTTON,
        id: "prize-wheel-close",
      },
    });

    setShowPrizeWheel(false);
  };

  if (isOnboardingCompleted) return null;

  // Referral flow with Prize Wheel Modal
  if (referralCode && isLoggedOut) {
    return (
      <OnboardingPrizeWheelModal
        open={showPrizeWheel}
        onOpenChange={(open) => {
          if (!open) {
            handleClosePrizeWheel();
          }
        }}
        onComplete={handlePrizeWheelComplete}
      />
    );
  }

  // Normal onboarding flows
  if (!referralCode) return <OnboardingContent signUpTitle={signUpTitle} />;

  return null;
}
